// src/validators.js
// Shared validation helpers so signup, customer, and appointment forms
// all apply the same rules. Keep this file small and dependency-free.

// Names: allow letters, spaces, hyphen, apostrophe, period. Reject if entirely
// whitespace or contains disallowed chars like !@#$%^&* etc.
export function isValidName(value) {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  // Allow basic name chars, common accented letters, hyphen, apostrophe, period, space
  return /^[\p{L}][\p{L}\s.'-]{0,99}$/u.test(trimmed);
}

export function nameError(value, label = 'Name') {
  if (!value || !value.trim()) return `${label} is required`;
  if (!isValidName(value)) return `${label} can only contain letters, spaces, hyphens, and apostrophes`;
  return null;
}

// US phone: accept 10 digits OR 11 digits starting with 1. Strip separators.
export function isValidPhone(value, { allowEmpty = false } = {}) {
  if (!value || !value.trim()) return allowEmpty;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits.startsWith('1')) return true;
  return false;
}

export function phoneError(value, { allowEmpty = false, label = 'Phone' } = {}) {
  if (!value || !value.trim()) return allowEmpty ? null : `${label} is required`;
  if (!isValidPhone(value, { allowEmpty })) return `Enter a valid US phone number (10 digits)`;
  return null;
}

// Street address: letters, numbers, spaces, common punctuation (# . , / -). Reject
// strings that are only special chars or entirely whitespace.
export function isValidAddressLine(value) {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  // Must contain at least one letter or digit
  if (!/[\p{L}\d]/u.test(trimmed)) return false;
  return /^[\p{L}\d\s.,#/'-]{1,200}$/u.test(trimmed);
}

export function addressError(value, { allowEmpty = false, label = 'Address' } = {}) {
  if (!value || !value.trim()) return allowEmpty ? null : `${label} is required`;
  if (!isValidAddressLine(value)) return `${label} contains invalid characters`;
  return null;
}

// City / locality: similar but no #, /, or digits required
export function isValidCity(value) {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /^[\p{L}][\p{L}\s.'-]{0,99}$/u.test(trimmed);
}

export function cityError(value, { allowEmpty = false } = {}) {
  if (!value || !value.trim()) return allowEmpty ? null : `City is required`;
  if (!isValidCity(value)) return `City can only contain letters, spaces, hyphens, and apostrophes`;
  return null;
}

// State: 2-letter US code OR full name
export function isValidState(value) {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^[A-Za-z]{2}$/.test(trimmed)) return true;
  return /^[\p{L}][\p{L}\s-]{1,49}$/u.test(trimmed);
}

export function stateError(value, { allowEmpty = false } = {}) {
  if (!value || !value.trim()) return allowEmpty ? null : `State is required`;
  if (!isValidState(value)) return `Enter a valid state (e.g. AZ or Arizona)`;
  return null;
}

// Email: basic shape check. Deliberately loose — the auth provider rejects bad ones.
export function isValidEmail(value) {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

export function emailError(value, { allowEmpty = false } = {}) {
  if (!value || !value.trim()) return allowEmpty ? null : `Email is required`;
  if (!isValidEmail(value)) return `Enter a valid email address`;
  return null;
}

// Format a phone as (###) ###-#### when possible
export function formatPhone(value) {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
}
